import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  orderBy,
  limit,
  doc,
  updateDoc,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { db } from "../config/firebase";
import { awardTradePoints } from "./pointsService";

/**
 * Create a new price prediction bet
 * @param {object} betData - Prediction bet data
 * @returns {Promise<string>} Document ID
 */
export const createPrediction = async (betData) => {
  try {
    const {
      walletAddress,
      symbol,
      direction,
      currentPrice,
      targetPrice,
      amount,
      leverage = 1,
      expiry,
      duration,
      transactionHash,
      onChain = false,
      confirmed = false,
      contractPredictionId = null,
    } = betData;

    if (!walletAddress || !symbol || !direction || !currentPrice || !amount) {
      throw new Error("Missing required fields for prediction");
    }

    const totalVolume = amount * leverage;

    const predictionData = {
      walletAddress: walletAddress.toLowerCase(),
      symbol: symbol.toUpperCase(),
      direction: direction.toUpperCase(),
      currentPrice: Number(currentPrice),
      targetPrice: targetPrice ? Number(targetPrice) : null,
      amount: Number(amount),
      leverage: Number(leverage),
      totalVolume: Number(totalVolume),
      expiry: expiry ? (typeof expiry === "number" ? expiry : expiry.getTime()) : null,
      duration: duration ? Number(duration) : null, // Duration in seconds
      status: "active",
      transactionHash: transactionHash || "",
      onChain: Boolean(onChain),
      confirmed: Boolean(confirmed),
      contractPredictionId: contractPredictionId !== null ? Number(contractPredictionId) : null,
      timestamp: serverTimestamp(),
      createdAt: new Date().toISOString(),
    };

    const docRef = await addDoc(collection(db, "priceBets"), predictionData);
    
    // Award points for making a prediction
    try {
      await awardTradePoints(
        walletAddress,
        amount,
        'prediction',
        {
          symbol,
          direction,
          predictionId: docRef.id,
        }
      );
    } catch (pointsError) {
      console.error("Error awarding prediction points:", pointsError);
      // Don't fail the prediction if points fail
    }
    
    return docRef.id;
  } catch (error) {
    console.error("Error creating prediction:", error);
    throw error;
  }
};

/**
 * Get all predictions for a wallet
 * @param {string} walletAddress - Wallet address
 * @returns {Promise<Array>} Array of predictions (sorted by timestamp desc)
 */
export const getUserPredictions = async (walletAddress) => {
  try {
    if (!walletAddress) return [];

    const normalizedAddress = walletAddress.toLowerCase();
    
    try {
      // Try query with index first
    const q = query(
      collection(db, "priceBets"),
      where("walletAddress", "==", normalizedAddress),
      orderBy("timestamp", "desc")
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    } catch (error) {
      // If index error, fallback to filtering and sorting in memory
      if (error.code === 'failed-precondition' || error.message?.includes('index')) {
        console.warn("Firestore index not found, using fallback query. Create the index at:", error.message?.match(/https:\/\/[^\s]+/)?.[0] || "Firebase Console");
        
        try {
          // Fetch all predictions for this wallet without orderBy
          const q = query(
            collection(db, "priceBets"),
            where("walletAddress", "==", normalizedAddress)
          );

          const querySnapshot = await getDocs(q);
          const predictions = querySnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));

          // Sort in memory by timestamp
          predictions.sort((a, b) => {
            const timeA = a.timestamp?.toDate?.()?.getTime() || a.timestamp?.getTime() || a.createdAt ? new Date(a.createdAt).getTime() : 0;
            const timeB = b.timestamp?.toDate?.()?.getTime() || b.timestamp?.getTime() || b.createdAt ? new Date(b.createdAt).getTime() : 0;
            return timeB - timeA; // Descending order
          });

          return predictions;
        } catch (fallbackError) {
          console.error("Error in fallback query:", fallbackError);
          throw fallbackError;
        }
      }
      
      // Re-throw if it's not an index error
      throw error;
    }
  } catch (error) {
    console.error("Error getting user predictions:", error);
    throw error;
  }
};

/**
 * Get all active predictions
 * @returns {Promise<Array>} Array of active predictions
 */
export const getAllActivePredictions = async () => {
  try {
    const q = query(
      collection(db, "priceBets"),
      where("status", "==", "active"),
      orderBy("timestamp", "desc")
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error("Error getting active predictions:", error);
    throw error;
  }
};


export const getPredictionsBySymbol = async (symbol) => {
  try {
    if (!symbol) return [];

    const q = query(
      collection(db, "priceBets"),
      where("symbol", "==", symbol.toUpperCase()),
      where("status", "==", "active"),
      orderBy("timestamp", "desc")
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error("Error getting predictions by symbol:", error);
    throw error;
  }
};


export const updatePredictionStatus = async (
  predictionId,
  status,
  finalPrice = null,
  profitLoss = null,
  additionalData = {}
) => {
  try {
    const predictionRef = doc(db, "priceBets", predictionId);
    const updateData = {
      status,
      updatedAt: serverTimestamp(),
    };

    if (finalPrice !== null) {
      updateData.exitPrice = Number(finalPrice);
      updateData.finalPrice = Number(finalPrice); // Keep for backward compatibility
    }

    if (profitLoss !== null) {
      updateData.profitAmount = Number(profitLoss);
      updateData.profitLoss = Number(profitLoss); // Keep for backward compatibility
    }

    if (status !== "active") {
      updateData.settledAt = serverTimestamp();
      updateData.closedAt = serverTimestamp();
    }

    // Add additional fields
    if (additionalData.profitPercent !== undefined) {
      updateData.profitPercent = Number(additionalData.profitPercent);
    }
    if (additionalData.finalAmount !== undefined) {
      updateData.finalAmount = Number(additionalData.finalAmount);
    }
    if (additionalData.isWinning !== undefined) {
      updateData.isWinning = Boolean(additionalData.isWinning);
    }
    if (additionalData.closedManually !== undefined) {
      updateData.closedManually = Boolean(additionalData.closedManually);
    }

    await updateDoc(predictionRef, updateData);
  } catch (error) {
    console.error("Error updating prediction status:", error);
    throw error;
  }
};

/**
 * Claim winnings for a won prediction
 * @param {string} predictionId - Document ID
 * @returns {Promise<void>}
 */
export const claimWinnings = async (predictionId) => {
  try {
    const predictionRef = doc(db, "priceBets", predictionId);
    await updateDoc(predictionRef, {
      claimed: true,
      claimedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error("Error claiming winnings:", error);
    throw error;
  }
};

/**
 * Get prediction statistics for a user
 * @param {string} walletAddress - Wallet address
 * @returns {Promise<object>} Statistics object
 */
export const getUserStats = async (walletAddress) => {
  try {
    const predictions = await getUserPredictions(walletAddress);

    const stats = {
      total: predictions.length,
      active: 0,
      won: 0,
      lost: 0,
      expired: 0,
      totalInvested: 0,
      totalProfit: 0,
      winRate: 0,
    };

    predictions.forEach((pred) => {
      if (pred.status === "active") stats.active++;
      else if (pred.status === "won") stats.won++;
      else if (pred.status === "lost") stats.lost++;
      else if (pred.status === "expired") stats.expired++;

      stats.totalInvested += pred.amount || 0;
      stats.totalProfit += pred.profitLoss || 0;
    });

    const completed = stats.won + stats.lost;
    stats.winRate = completed > 0 ? (stats.won / completed) * 100 : 0;

    return stats;
  } catch (error) {
    console.error("Error getting user stats:", error);
    throw error;
  }
};

/**
 * Get recent predictions for activity feed
 * @param {number} count - Number of predictions to return
 * @returns {Promise<Array>} Array of recent predictions
 */
export const getRecentPredictions = async (count = 10) => {
  try {
    const q = query(
      collection(db, "priceBets"),
      orderBy("timestamp", "desc"),
      limit(count)
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error("Error getting recent predictions:", error);
    throw error;
  }
};

/**
 * Calculate market sentiment for a crypto
 * @param {string} symbol - Crypto symbol
 * @returns {Promise<object>} Sentiment object
 */
export const getMarketSentiment = async (symbol) => {
  try {
    const predictions = await getPredictionsBySymbol(symbol);

    const sentiment = {
      bullishCount: 0,
      bearishCount: 0,
      bullishPercent: 0,
      bearishPercent: 0,
      bullishVolume: 0,
      bearishVolume: 0,
      totalVolume: 0,
      totalPredictions: predictions.length,
    };

    predictions.forEach((pred) => {
      if (pred.direction === "ABOVE" || pred.direction === "UP") {
        sentiment.bullishCount++;
        sentiment.bullishVolume += pred.totalVolume || pred.amount || 0;
      } else if (pred.direction === "BELOW" || pred.direction === "DOWN") {
        sentiment.bearishCount++;
        sentiment.bearishVolume += pred.totalVolume || pred.amount || 0;
      }
      sentiment.totalVolume += pred.totalVolume || pred.amount || 0;
    });

    if (sentiment.totalPredictions > 0) {
      sentiment.bullishPercent =
        (sentiment.bullishCount / sentiment.totalPredictions) * 100;
      sentiment.bearishPercent =
        (sentiment.bearishCount / sentiment.totalPredictions) * 100;
    }

    return sentiment;
  } catch (error) {
    console.error("Error getting market sentiment:", error);
    throw error;
  }
};

/**
 * Automatically settle expired predictions
 * @param {number} currentPrice - Current price of the asset
 * @param {string} symbol - Crypto symbol
 * @returns {Promise<number>} Number of settled predictions
 */
export const settleExpiredPredictions = async (currentPrice, symbol) => {
  try {
    const predictions = await getPredictionsBySymbol(symbol);
    const now = Date.now();
    let settledCount = 0;

    for (const pred of predictions) {
      if (pred.status !== "active" || !pred.expiry) continue;

      const expiryTime = typeof pred.expiry === "number" ? pred.expiry : pred.expiry.toMillis();
      
      if (now >= expiryTime) {
        // Prediction expired, determine win/loss
        let status = "expired";
        let profitLoss = -pred.amount; // Default loss

        if (pred.direction === "ABOVE" || pred.direction === "UP") {
          if (currentPrice >= (pred.targetPrice || pred.currentPrice)) {
            status = "won";
            profitLoss = pred.amount * (pred.leverage || 1) * 0.95; // 95% return
          } else {
            status = "lost";
          }
        } else if (pred.direction === "BELOW" || pred.direction === "DOWN") {
          if (currentPrice <= (pred.targetPrice || pred.currentPrice)) {
            status = "won";
            profitLoss = pred.amount * (pred.leverage || 1) * 0.95;
          } else {
            status = "lost";
          }
        }

        await updatePredictionStatus(pred.id, status, currentPrice, profitLoss);
        settledCount++;
      }
    }

    return settledCount;
  } catch (error) {
    console.error("Error settling expired predictions:", error);
    throw error;
  }
};

